<template>
  <DialogRoot :open="isOpen" @update:open="updateOpen">
    <DialogTrigger v-if="showTrigger" as-child>
      <slot name="trigger">
        <button :variant="'outline'">Open</button>
      </slot>
    </DialogTrigger>
    <DialogPortal>
      <DialogOverlay
        class="fixed inset-0 z-[9999] bg-black/40 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
      />
      <DialogContent
        class="[&[data-state='open']_.content]:animate-contentShow [&[data-state='open']_.header]:slide-in-from-top-1 relative"
        @escape-key-down="preventDismiss"
        @pointer-down-outside="preventDismiss"
      >
        <div :class="modal_variants({ size, padding })">
          <DialogClose
            v-if="showClose"
            class="text-black hover:bg-neutral-50/10 focus:shadow-neutral-50/50 ml-auto inline-flex h-[25px] transition-transform hover:scale-90 w-[25px] appearance-none items-center justify-center rounded-full focus:shadow-[0_0_0_2px] focus:outline-none absolute top-5 right-4"
            aria-label="Close"
          >
            <X />
          </DialogClose>

          <div>
            <DialogTitle>
              <slot name="title">
                <div :class="cn(title ? 'text-2xl font-bold text-primary mb-4' : 'sr-only')">
                  {{ title ?? 'Modal Title' }}
                </div>
              </slot>
            </DialogTitle>

            <DialogDescription>
              <slot name="description">
                <div :class="cn(description ? 'text-neutral-600 mb-4' : 'sr-only')">
                  {{ description ?? 'Modal Description' }}
                </div>
              </slot>
            </DialogDescription>

            <slot :toggle><span class="sr-only">Modal Content</span></slot>

            <div v-if="$slots.footer" class="mt-6 flex items-center justify-between dialog-footer">
              <slot name="footer" />
            </div>
          </div>
        </div>
      </DialogContent>
    </DialogPortal>
  </DialogRoot>
</template>

<script setup lang="ts">
  import { cn } from '@/utils/style'
  import { X } from 'lucide-vue-next'
  import { cva, type VariantProps } from 'class-variance-authority'

  const modal_variants = cva(
    'content fixed bg-gray-900 overflow-auto top-[50%] left-[50%] w-[90vw] translate-x-[-50%] translate-y-[-50%] rounded-br-xl rounded-tl-xl bg-neutral-100 shadow-[hsl(206_22%_7%_/_35%)_0px_10px_38px_-10px,_hsl(206_22%_7%_/_20%)_0px_10px_20px_-15px] focus:outline-none z-[10000]',
    {
      variants: {
        size: {
          xl: 'max-w-4xl max-h-[95vh]',
          lg: 'max-w-3xl max-h-[90vh]',
          md: 'max-w-xl max-h-[75vh]',
          sm: 'max-w-[500px] max-h-[55vh]',
        },
        padding: {
          none: 'p-0',
          default: 'p-6 sm:p-10',
        },
      },

      defaultVariants: {
        size: 'lg',
        padding: 'default',
      },
    },
  )

  type ModalVariant = VariantProps<typeof modal_variants>

  const {
    showClose = false,
    size = 'md',
    padding = 'default',
    controlled = true,
    showTrigger = false,
    forceClose = false,
    title = '',
    description = '',
  } = defineProps<{
    showClose?: boolean
    size?: ModalVariant['size']
    padding?: 'none' | 'default'
    controlled?: boolean
    showTrigger?: boolean
    title?: string
    description?: string
    forceClose?: boolean
  }>()

  const model = defineModel<boolean>({ default: false })
  const modal_open = ref(false)

  const isOpen = computed(() => (controlled ? model.value : modal_open.value))
  useFormStoreRadixOverlay(isOpen)

  const setOpen = (open: boolean) => {
    if (controlled) {
      model.value = open
      return
    }

    modal_open.value = open
  }

  const preventDismiss = (event: Event) => {
    if (forceClose) {
      event.preventDefault()
    }
  }

  const updateOpen = (open: boolean) => {
    if (!open && forceClose) {
      return
    }

    setOpen(open)
  }

  const toggle = () => {
    updateOpen(!isOpen.value)
  }
</script>
